import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import Cookies from 'js-cookie';

type Language = 'pt' | 'en';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  toggleLanguage: () => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Helper function to check if cookie consent is accepted
const isCookieConsentAccepted = (): boolean => {
  return Cookies.get('cookieConsent') === 'accepted';
};

interface LanguageProviderProps {
  children: ReactNode;
  cookieConsentProvided?: boolean;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children, cookieConsentProvided = false }) => {
  // Get language from cookie or use default (pt)
  const [language, setLanguageState] = useState<Language>(
    () => (Cookies.get('language') as Language) || 'pt'
  );

  // Determine if we should save cookies based on consent
  const shouldSaveCookies = isCookieConsentAccepted() || cookieConsentProvided;

  // Update cookie when language changes, but only if consent is given
  useEffect(() => {
    if (shouldSaveCookies) {
    Cookies.set('language', language, { expires: 365 }); // Expires in 1 year
    }
  }, [language, shouldSaveCookies]);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
  };

  const toggleLanguage = () => {
    setLanguageState(prevLang => prevLang === 'pt' ? 'en' : 'pt');
  };

  const value = {
    language,
    setLanguage,
    toggleLanguage
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext; 